import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { ClientTherapistRelationship } from "@/types/therapist";
import { toast } from "sonner";

export const useTherapistClients = () => {
  const { data: roleData, isLoading: roleLoading } = useQuery({
    queryKey: ['therapist-role-check'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        toast.error("You must be logged in to access this page");
        return null;
      }
      
      const { data, error } = await supabase
        .from('user_roles')
        .select('*')
        .eq('user_id', user.id)
        .eq('role', 'therapist')
        .maybeSingle();
        
      if (error) throw error;
      
      if (!data) {
        toast.error("You do not have permission to access this page");
        return null;
      }
      
      return data;
    }
  });

  const { data: clients, isLoading: clientsLoading, refetch: refetchClients } = useQuery({
    queryKey: ['therapist-clients'],
    queryFn: async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        
        if (!user) return [];
        
        const { data: relationships, error } = await supabase
          .from('client_therapist_relationships')
          .select('*')
          .eq('therapist_id', user.id);
        
        if (error) throw error;
        
        if (!relationships || relationships.length === 0) {
          return [];
        }
        
        const clientsWithDetails = await Promise.all(
          relationships.map(async (relationship) => {
            let clientInfo = {
              email: `Client ID: ${relationship.client_id.substring(0, 8)}...`,
              user_metadata: { 
                name: "", 
                full_name: "", 
                first_name: "", 
                last_name: "",
                company: ""
              },
              profile: {
                user_id: relationship.client_id,
                company: null,
                first_name: null,
                last_name: null
              }
            };
            
            try {
              const { data: profileData, error: profileError } = await supabase
                .from('user_profiles')
                .select('company, first_name, last_name')
                .eq('user_id', relationship.client_id)
                .maybeSingle();
              
              if (!profileError && profileData) {
                clientInfo.profile = {
                  user_id: relationship.client_id,
                  company: profileData.company,
                  first_name: profileData.first_name,
                  last_name: profileData.last_name
                };
                console.log("Found profile data for client:", relationship.client_id, profileData);
              } else {
                console.log("No profile data found for client:", relationship.client_id, profileError);
              }
              
              const { data: authData, error: authError } = await supabase
                .from('user_roles')  // Just using this to get auth status, not for actual data
                .select('role')
                .limit(1);
                
              if (authError) {
                console.error("Error checking auth status:", authError);
              } else {
                // If we're authenticated, try to get some basic client info
                try {
                  // Try to get any journal entries to at least confirm the user exists
                  const { data: entries } = await supabase
                    .from('journal_entries')
                    .select('user_id')
                    .eq('user_id', relationship.client_id)
                    .limit(1);
                    
                  if (entries && entries.length > 0) {
                    console.log("Found journal entries for client:", relationship.client_id);
                  }
                } catch (err) {
                  console.error("Error checking client journal entries:", err);
                }
              }
              
              const hasProfileInfo = clientInfo.profile && 
                (clientInfo.profile.first_name || clientInfo.profile.last_name || clientInfo.profile.company);
                
              if (!hasProfileInfo) {
                console.log("Falling back to cached client data");
                const storedClients = localStorage.getItem('clientsInfo');
                if (storedClients) {
                  const parsedClients = JSON.parse(storedClients);
                  if (parsedClients[relationship.client_id]) {
                    return {
                      ...relationship,
                      client: parsedClients[relationship.client_id]
                    };
                  }
                }
              }
            } catch (error) {
              console.error("Error fetching client details:", error);
            }
            
            return {
              ...relationship,
              client: clientInfo
            };
          })
        );
        
        const clientsCache: {[clientId: string]: any} = {};
        clientsWithDetails.forEach(client => {
          if (client.client) {
            clientsCache[client.client_id] = client.client;
          }
        });
        localStorage.setItem('clientsInfo', JSON.stringify(clientsCache));
        
        console.log("Clients with details:", clientsWithDetails);
        return clientsWithDetails as ClientTherapistRelationship[];
      } catch (error) {
        console.error("Error fetching client relationships:", error);
        toast.error("Failed to load clients");
        return [];
      }
    },
    enabled: !!roleData
  });

  return {
    roleData,
    roleLoading,
    clients,
    clientsLoading,
    refetchClients
  };
};
